import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Database,
  ScrollText,
  CreditCard,
  Bell,
  BarChart3,
  Settings,
  LogOut,
  Scale,
} from "lucide-react";
import { motion } from "framer-motion";
import HelpModal from "@/components/jea/HelpModal";
import { useAuth } from "@/context/AuthContext";
import { apiClient } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/admin",              label: "Overview",         icon: LayoutDashboard, end: true },
  { to: "/admin/users",        label: "User Management",  icon: Users },
  { to: "/admin/brokers",      label: "Broker Management",icon: Database },
  { to: "/admin/renewal-rules",label: "Renewal Rules",    icon: ScrollText },
  { to: "/admin/subscriptions",label: "Subscriptions",    icon: CreditCard },
  { to: "/admin/alerts",       label: "Alerts",           icon: Bell,    badge: "alerts" },
  { to: "/admin/reports",      label: "Reports",          icon: BarChart3 },
  { to: "/admin/settings",     label: "Settings",         icon: Settings },
];

const TITLES = {
  "/admin": "Overview",
  "/admin/users": "User Management",
  "/admin/brokers": "Broker Management",
  "/admin/renewal-rules": "Renewal Rules",
  "/admin/subscriptions": "Subscriptions",
  "/admin/alerts": "Alerts",
  "/admin/reports": "Reports",
  "/admin/settings": "Settings",
};

const initialsOf = (name) => {
  if (!name) return "AD";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

const AdminShell = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [pendingAlerts, setPendingAlerts] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const title = TITLES[location.pathname] || "Admin";

  const refreshAlerts = async () => {
    try {
      const { data } = await apiClient.get("/admin/alerts/count");
      setPendingAlerts(data.pending || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshAlerts();
    const t = setInterval(refreshAlerts, 30_000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bg-primary text-white flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        className="w-[240px] shrink-0 bg-bg-secondary border-r border-border-default flex flex-col h-screen sticky top-0"
        data-testid="admin-sidebar"
      >
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-2">
            <Scale className="text-accent-pink" size={20} strokeWidth={2.5} />
            <span className="font-extrabold text-[16px] text-accent-pink tracking-tight">
              Judgment Enforcement AI
            </span>
          </div>
          <div className="mt-3">
            <span
              className="inline-flex items-center px-2 py-[3px] rounded-[4px] border border-danger text-danger text-[10px] font-bold tracking-[0.10em]"
              data-testid="admin-sidebar-badge"
            >
              ADMIN
            </span>
          </div>
        </div>
        <div className="h-px w-full bg-accent-pink/20" />

        <nav className="flex-1 overflow-y-auto py-2" data-testid="admin-sidebar-nav">
          {NAV.map(({ to, label, icon: Icon, badge, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-3 h-12 pl-4 pr-3 text-[14px] font-medium transition-all duration-150",
                  isActive
                    ? "bg-bg-tertiary text-accent-pink"
                    : "text-text-secondary hover:bg-bg-tertiary hover:text-white"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-accent-pink" />
                  )}
                  <Icon size={18} className={isActive ? "text-accent-pink" : ""} />
                  <span className="truncate">{label}</span>
                  {badge === "alerts" && pendingAlerts > 0 && (
                    <span
                      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold bg-danger text-white animate-pulse-alert"
                      data-testid="admin-sidebar-alerts-count"
                    >
                      {pendingAlerts > 9 ? "9+" : pendingAlerts}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-border-default">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full bg-accent-pink text-[#091424] flex items-center justify-center font-bold text-[13px]"
              data-testid="admin-avatar"
            >
              {initialsOf(user?.full_name || "AD")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[14px] font-semibold truncate">
                {user?.full_name || "Admin"}
              </div>
              <div className="text-danger text-[10px] font-bold tracking-[0.08em]" data-testid="admin-role">
                PLATFORM ADMIN
              </div>
            </div>
            <button
              type="button"
              onClick={doLogout}
              className="p-2 rounded-md text-text-secondary hover:text-danger hover:bg-danger/10 transition-colors"
              title="Sign out"
              data-testid="admin-logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="h-16 flex items-center justify-between px-8 border-b border-border-default bg-bg-primary sticky top-0 z-30"
          data-testid="admin-header"
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center px-2 py-[3px] rounded-[4px] bg-danger/15 border border-danger/40 text-danger text-[11px] font-bold tracking-[0.10em]"
              data-testid="admin-header-badge"
            >
              ADMIN
            </span>
            <h1 className="text-white font-bold text-[20px]" data-testid="admin-page-title">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="p-2 rounded-full text-text-secondary hover:text-accent-pink hover:bg-bg-secondary transition-colors"
              title="Help"
              data-testid="admin-help"
            >
              <span className="inline-flex items-center justify-center w-5 h-5">?</span>
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="max-w-[1320px] mx-auto px-8 py-8"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};

export default AdminShell;
